import React, { useState, useEffect } from 'react';
import { blogsApi, documentsApi, wordpressApi } from '../services/api';
import { Plus, Edit2, Trash2, Globe, Key, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import './AdminBlogs.css';

function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  const [form, setForm] = useState({
    blog_id: '',
    domain: ''
  });

  // WordPress token management state
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const response = await blogsApi.list();
      setBlogs(response.data);
    } catch (error) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.blog_id) {
      toast.error('WordPress Blog ID is required');
      return;
    }

    if (!form.domain) {
      toast.error('Domain is required');
      return;
    }

    try {
      if (editingBlog) {
        await blogsApi.update(editingBlog.id, {
          blog_id: parseInt(form.blog_id),
          domain: form.domain
        });
        toast.success('Blog updated');
      } else {
        await blogsApi.create({
          blog_id: parseInt(form.blog_id),
          domain: form.domain
        });
        toast.success('Blog created');
      }
      setShowModal(false);
      setEditingBlog(null);
      setForm({ blog_id: '', domain: '' });
      loadBlogs();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = async (blog) => {
    setEditingBlog(blog);
    setForm({
      blog_id: blog.blog_id.toString(),
      domain: blog.domain
    });
    setTokenInput('');
    setTokenInfo(null);
    setShowModal(true);

    // Load token info for this blog
    await loadTokenInfo(blog.id);
  };

  const loadTokenInfo = async (blogId) => {
    setTokenLoading(true);
    try {
      const response = await wordpressApi.getTokenInfo(blogId);
      setTokenInfo(response.data);
    } catch (error) {
      console.error('Failed to load token info:', error);
      setTokenInfo({ hasToken: false });
    } finally {
      setTokenLoading(false);
    }
  };

  const handleSaveToken = async () => {
    if (!tokenInput.trim()) {
      toast.error('Please enter a JWT token');
      return;
    }

    setTokenLoading(true);
    try {
      await wordpressApi.setTokenManually(editingBlog.id, tokenInput.trim());
      toast.success('Token saved successfully');
      setTokenInput('');
      await loadTokenInfo(editingBlog.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save token');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await wordpressApi.testConnection(editingBlog.id);
      if (response.data.connected) {
        toast.success('WordPress connection successful!');
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDelete = async (blog) => {
    try {
      // First fetch the document count for this blog
      const docsResponse = await documentsApi.list(blog.id, { limit: 1 });
      const documentCount = docsResponse.data.pagination?.total || 0;

      // Build confirmation message with document count
      let message = `Are you sure you want to delete "${blog.domain}"?`;
      if (documentCount > 0) {
        message += `\n\nThis will permanently delete ${documentCount} document${documentCount === 1 ? '' : 's'} associated with this blog.`;
      } else {
        message += `\n\nThis blog has no documents.`;
      }

      if (!confirm(message)) return;

      await blogsApi.delete(blog.id);
      toast.success('Blog deleted');
      loadBlogs();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete blog');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner" />
        <p>Loading blogs...</p>
      </div>
    );
  }

  return (
    <div className="admin-blogs">
      <div className="admin-header">
        <h1>Blog Management</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add Blog
        </button>
      </div>

      <div className="blogs-table-container">
        <table className="blogs-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>WordPress Blog ID</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.length === 0 ? (
              <tr>
                <td colSpan="4" className="empty-state">
                  <Globe size={48} />
                  <p>No blogs configured yet</p>
                  <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                    <Plus size={16} />
                    Create your first blog
                  </button>
                </td>
              </tr>
            ) : (
              blogs.map(blog => (
                <tr key={blog.id}>
                  <td>
                    <div className="blog-domain">
                      <Globe size={16} />
                      {blog.domain}
                    </div>
                  </td>
                  <td>{blog.blog_id}</td>
                  <td>{new Date(blog.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-icon"
                        onClick={() => handleEdit(blog)}
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="btn btn-icon btn-danger"
                        onClick={() => handleDelete(blog)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingBlog ? 'Edit Blog' : 'Add Blog'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="domain">Domain</label>
                <input
                  type="text"
                  id="domain"
                  value={form.domain}
                  onChange={(e) => setForm(f => ({ ...f, domain: e.target.value }))}
                  placeholder="e.g., example.cemsites.com"
                  required
                />
                <small>The WordPress site domain (used as display name)</small>
              </div>
              <div className="form-group">
                <label htmlFor="blog_id">WordPress Blog ID</label>
                <input
                  type="number"
                  id="blog_id"
                  value={form.blog_id}
                  onChange={(e) => setForm(f => ({ ...f, blog_id: e.target.value }))}
                  placeholder="e.g., 271"
                  required={!editingBlog}
                  disabled={!!editingBlog}
                  className={editingBlog ? 'input-disabled' : ''}
                />
                <small>{editingBlog ? 'Blog ID cannot be changed after creation' : 'Numeric identifier for WordPress multisite'}</small>
              </div>

              {/* WordPress Integration Section - only shown when editing */}
              {editingBlog && (
                <div className="wordpress-integration-section">
                  <h3>
                    <Key size={18} />
                    WordPress Integration
                  </h3>

                  {/* Current token status */}
                  <div className="token-status">
                    {tokenLoading ? (
                      <div className="token-loading">
                        <RefreshCw size={16} className="spinning" />
                        Loading token info...
                      </div>
                    ) : tokenInfo?.hasToken ? (
                      <div className="token-info">
                        <div className="token-status-badge success">
                          <CheckCircle size={16} />
                          Token configured
                        </div>
                        <div className="token-preview">
                          <code>{tokenInfo.tokenPreview}</code>
                        </div>
                        <div className="token-meta">
                          Last updated: {new Date(tokenInfo.lastUpdated).toLocaleString()}
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={handleTestConnection}
                          disabled={testingConnection}
                        >
                          {testingConnection ? (
                            <>
                              <RefreshCw size={14} className="spinning" />
                              Testing...
                            </>
                          ) : (
                            'Test Connection'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="token-status-badge warning">
                        <XCircle size={16} />
                        No token configured
                      </div>
                    )}
                  </div>

                  {/* Token input */}
                  <div className="token-input-section">
                    <label htmlFor="jwt_token">
                      {tokenInfo?.hasToken ? 'Update Token' : 'Enter JWT Token'}
                    </label>
                    <textarea
                      id="jwt_token"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      placeholder="Paste your WordPress JWT token here..."
                      rows={3}
                    />
                    <small>
                      To get your token: Log into WordPress, navigate to Doc Builder API settings, and click Copy Token.
                    </small>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveToken}
                      disabled={tokenLoading || !tokenInput.trim()}
                    >
                      {tokenLoading ? 'Saving and Verifying...' : 'Save Token'}
                    </button>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBlog(null);
                    setForm({ blog_id: '', domain: '' });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingBlog ? 'Save Changes' : 'Create Blog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBlogs;
